import { useState } from 'react'

import { useMutation } from '@apollo/client'
import { Checkbox, List, message, Typography } from 'antd'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import styled from 'styled-components'

import { UPDATE_TEMPLATE_RESPONSE } from '../../graphQL'
import { dynamicClone, findBlocks } from '../../templateEditor/editorHelper'
import { TodoDisplayBlock as TodoDisplayBlockType } from '../../types/blueprint'

import { useTemplateRenderer } from '@/context/templateRendererContext'

import {
  UpdateTemplateResponseUiMutation,
  UpdateTemplateResponseUiMutationVariables,
} from 'types/api.graphql'

const ListItem = styled(List.Item)`
  padding-left: 0 !important;
  padding-right: 0 !important;
`

const ListItemMeta = styled(List.Item.Meta)`
  & > .ant-list-item-meta-avatar {
    align-self: stretch;
    display: flex;
    align-items: center;
  }
`

const Text = styled(Typography.Text)<{ completed?: boolean }>`
  text-decoration: ${({ completed }) => (completed ? 'line-through' : 'none')};
`

const BasicList = styled.ul`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const BasicItem = styled.li<{ completed?: boolean }>`
  text-decoration: ${({ completed }) => (completed ? 'line-through' : 'none')};
`

type Props = {
  block: TodoDisplayBlockType
}

export const TodoDisplayBlock: React.FC<Props> = ({ block }) => {
  const { templateResponseId, templateResponseData } = useTemplateRenderer()
  const [data, setData] = useState(block.todos)

  const [updateTemplateResponse] = useMutation<
    UpdateTemplateResponseUiMutation,
    UpdateTemplateResponseUiMutationVariables
  >(UPDATE_TEMPLATE_RESPONSE)

  if (!data?.length) {
    return <Typography.Text>No todo&apos;s yet</Typography.Text>
  }

  const complete = (index: number) => async (e: CheckboxChangeEvent) => {
    const newValue = [...data]
    newValue[index] = { ...newValue[index], complete: e.target.checked }

    const responseData = dynamicClone('TEMPLATE', templateResponseData)
    const todoBlock = findBlocks(
      responseData,
      (b) => b.id === block.id
    )[0] as TodoDisplayBlockType

    todoBlock.todos = newValue

    try {
      await updateTemplateResponse({
        variables: { id: templateResponseId, input: { responseData } },
      })

      setData(newValue)
    } catch {
      message.error(`Couldn't update todo. Try in edit mode.`)
      setData([...block.todos])
    }
  }

  if (templateResponseId) {
    return (
      <List
        size="small"
        dataSource={data}
        renderItem={(todo, index) => (
          <ListItem>
            <ListItemMeta
              avatar={
                <Checkbox
                  defaultChecked={todo.complete}
                  onChange={complete(index)}
                />
              }
              description={<Text completed={todo.complete}>{todo.label}</Text>}
            />
          </ListItem>
        )}
      />
    )
  }

  return (
    <BasicList>
      {data.map(({ label, complete }, index) => (
        <BasicItem key={index} completed={complete}>
          {label}
        </BasicItem>
      ))}
    </BasicList>
  )
}
